import isNil from 'lodash/isNil';
import {
  InsuranceDTO,
  MultiMediaResponse,
  PayerEntryDTO,
  ProviderContextDTO,
} from 'dtos';
import {
  InsuranceSlot,
} from 'dtos/insuranceSlot';
import {
  DecimalNullableRangeFilterDTO,
} from 'dtos/decimalNullableRangeFilterDTO';
import dayjs from 'utils/dayjs';
import {
  pluralize,
  getFileExtension,
  isEmptyString,
  notEmptyString,
  getNumberFromValue,
} from '@populateui/utils';
import {
  getAddressString,
} from 'utils/string';
import compact from 'lodash/compact';
import {
  formatDate,
} from 'utils/date';
import {
  AddressForm,
} from 'pages/Dashboard/components/AddressFields';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';

export function uuid() {
  if (typeof crypto?.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  const bytes = typeof globalThis.crypto?.getRandomValues === 'function'
    ? globalThis.crypto.getRandomValues(new Uint8Array(16))
    : Uint8Array.from({ length: 16 }, () => Math.floor(Math.random() * 256));

  bytes[6] = (bytes[6] % 16) + 64;
  bytes[8] = (bytes[8] % 64) + 128;

  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export const em = (em: number = 1, relativeSize: number = 16) => em * relativeSize;

export function getMediaType(media: MultiMediaResponse): 'document' | 'image' {
  return getFileExtension(media.fileName ?? '') === 'pdf' ? 'document' : 'image';
}

export type DateUnit = 'year' | 'month' | 'week' | 'day';
const dateUnits: DateUnit[] = ['year', 'month', 'week', 'day'];
type AgeInformation = {
  value: number;
  unit: DateUnit;
};

export function getAge(dateOfBirth?: string, unit?: DateUnit): AgeInformation {
  let value = 0;
  let currentUnitIndex = -1;
  if (isNil(unit)) {
    while (value === 0 && currentUnitIndex < 3) {
      currentUnitIndex += 1;
      value = dayjs().diff(dateOfBirth, dateUnits[currentUnitIndex]);
    }
  } else {
    value = dayjs().diff(dateOfBirth, unit);
  }
  return {
    value,
    unit: unit ?? dateUnits[currentUnitIndex],
  };
}

export function getPCPBlurb({
  providerEntry,
  lastSeenOn,
}: ProviderContextDTO) {
  const indexName = getDisplayName(providerEntry?.formattedNames);
  const result = compact([
    indexName,
    providerEntry?.state,
    isEmptyString(lastSeenOn) ? null : `Last seen on ${formatDate(lastSeenOn ?? '')}`,
  ]).join(' • ');

  return isEmptyString(result) ? 'Not Set' : result;
}

export function getPatientAge(dateOfBirth?: string): string {
  const { value, unit } = getAge(dateOfBirth);
  return `${pluralize(unit, value)} old`;
}

export function formatPlace(value: AddressForm) {
  return {
    place: {
      description: getAddressString(value),
      structured_formatting:
      {
        main_text: value.street ?? '',
        secondary_text: compact([value.city, value.state, value.country]).join(' '),
        main_text_matched_substrings: [],
      },
    },
  };
}

export function getInsuranceBasicInfo(
  {
    payerId,
    isPaper,
    insuranceType,
    address,
  }: PayerEntryDTO,
  addAddress: boolean = false,
) {
  return compact([
    isPaper ? 'Paper' : null,
    payerId,
    insuranceType,
    addAddress && notEmptyString(address) ? getAddressString(address) : null,
  ]).join(' • ');
}

type InsuranceSlotConflictCheckParams = {
  currentInsuranceId?: number;
  selectedInsuranceSlot?: InsuranceSlot | null;
  isActiveInsurance: boolean;
};

export function getInsuranceSlotConflictValidator(
  insurances: InsuranceDTO[] = [],
  restrictedSlotSet: Set<InsuranceSlot | undefined> = new Set(),
) {
  return ({
    currentInsuranceId,
    selectedInsuranceSlot,
    isActiveInsurance,
  }: InsuranceSlotConflictCheckParams): InsuranceDTO | null => {
    if (
      !isActiveInsurance
      || isNil(selectedInsuranceSlot)
      || !restrictedSlotSet.has(selectedInsuranceSlot as InsuranceSlot)
    ) {
      return null;
    }
    return insurances.find((insuranceItem) => (
      (insuranceItem?.insuranceId ?? 0) !== (currentInsuranceId ?? 0)
      && !insuranceItem?.inactive
      && insuranceItem?.insuranceSlot === selectedInsuranceSlot
    )) ?? null;
  };
}

export function validateAmount(value: string) {
  return notEmptyString(value) && !Number.isNaN(Number(value)) && Number(value) > 0;
}

export function validateRange(range: DecimalNullableRangeFilterDTO) {
  const { min: minRawValue, max: maxRawValue } = range;
  const values = compact([minRawValue, maxRawValue]).map(getNumberFromValue);
  const [min, max] = values;
  const hasAllValues = values.length === 2;
  return hasAllValues ? max >= min : true;
}
